import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import PatientInfoRow from 'components/Patient/PatientInfoRow';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import _ from 'lodash';
import './style.scss';
import CalendarUtils from 'services/CalendarUtils';
import Icon from 'components/Utils/NewIcon';
import CPPUtils from 'services/CPPUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import NewImmunization from 'components/HealthRecords/Immunizations/NewImmunization';
import Collapse from 'components/Utils/Collapse';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import MiscUtils from 'services/MiscUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { UserRoleEnum } from 'generated/api';
import { DISEASES } from './diseases';

const YellowCardView = (props) => (
  <div className="yellow-card rounded" data-force-theme="light">
    {!props.hideTopBar && <TopRow {...props} />}
    <CardContent {...props} />
  </div>
);

YellowCardView.propTypes = {
  patient: PropTypes.object.isRequired,
  ownRole: PropTypes.string.isRequired,
  hideTopBar: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onGenerate: PropTypes.func,
  onClose: PropTypes.func,
  form: PropTypes.string,
  immunizations: PropTypes.array,
};

YellowCardView.defaultProps = {
  immunizations: [],
};

const TopRow = (props) => (
  <div className="top-row row">
    <PatientInfoRow patient={props.patient} />
    <Icon src="xmark" className="close-button white" onClick={props.onClose} />
  </div>
);

const CardContent = (props) => (
  <div className="card-content">
    <div className="row flex flex-wrap content-between py-5 px-5">
      <span className="large-title">Personal Immunization Record</span>
      <TopButtons {...props} />
    </div>
    <div className="scroll-list">
      {props.ownRole !== 'patient' && <AddImmunization {...props} />}
      <TableContent {...props} />
    </div>
  </div>
);

const AddImmunization = (props) => (
  <Collapse
    title="Add Immunization"
    collapsed={props.collapsed}
    onClick={() => props.setCollapsed(!props.collapsed)}
  >
    <div className="padded-box">
      <NewImmunization patient={props.patient} form={props.form} />
    </div>
  </Collapse>
);

const TableContent = (props) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [removing, setRemoving] = useState(false);

  function onClickedDelete(immunization) {
    setShowConfirmation(immunization);
  }

  return (
    <>
      {!!showConfirmation && (
        <ConfirmationDialog
          title="Remove Immunization"
          message="Are you sure you want to remove this immunization?"
          open={!!showConfirmation}
          onConfirm={async () => {
            setRemoving(true);
            await props.onDelete(showConfirmation);
            setRemoving(false);
            setShowConfirmation();
          }}
          loadingState={removing}
          onCancel={() => setShowConfirmation()}
        />
      )}
      <ReactTable
        key={props.immunizations.length}
        data={props.immunizations}
        defaultSorted={[
          {
            id: 'date',
            desc: false,
          },
        ]}
        columns={getColumns({ ...props, onClickedDelete })}
        defaultPageSize={props.immunizations.length || 25}
        className="-striped -highlight"
      />
    </>
  );
};

function getColumns(props) {
  let columns = [
    {
      Header: '',
      show: false,
      id: 'date',
      accessor: (v) => v.value.given_date || v.value.scheduled_date,
    },
    {
      Header: 'Given Date',
      id: 'given_date',
      maxWidth: 120,
      minWidth: 120,
      accessor: (v) => v.value.given_date || v.value.scheduled_date,
      Cell: (row) => <DateCell immunization={row.original} />,
    },
    {
      Header: 'Age',
      id: 'age',
      minWidth: 110,
      maxWidth: 110,
      accessor: (v) => {
        const date = v.value.given_date || v.value.scheduled_date;
        const { date_of_birth } = props.patient || {};
        if (date_of_birth !== undefined && date_of_birth !== 0) {
          return CalendarUtils.calculateAgeNumber(date_of_birth, date, 'days');
        }
        return -1;
      },
      Cell: (row) => <AgeCell immunization={row.original} patient={props.patient} />,
      className: 'center-text',
    },
    {
      Header: 'Vaccine',
      id: 'name',
      minWidth: 170,
      maxWidth: 170,
      accessor: ({ value }) => {
        const name = value.name === 'Other' ? value.description : value.name;
        return value.prescription ? `${value.prescription} (${name})` : name;
      },
      className: 'center-text',
    },
    {
      Header: <VaccineNames {...props} />,
      columns: getVaccinesColumns(),
    },
    {
      Header: 'Description',
      id: 'description',
      accessor: (v) => v.value.description || '',
      Cell: (row) => <span title={row.value}>{row.value}</span>,
      className: 'center-text',
    },
    {
      Header: 'Lot #',
      id: 'lot',
      accessor: (v) => v.value.lot || '',
      Cell: (row) => <span title={row.value}>{row.value}</span>,
      className: 'center-text',
    },
    {
      Header: 'Route',
      id: 'route',
      accessor: (v) => v.value.route || '',
      Cell: (row) => <span title={row.value}>{row.value}</span>,
      className: 'center-text',
    },
    {
      Header: 'Provider',
      id: 'provider',
      accessor: (v) => v.value.given_by || '',
      Cell: (row) => <span title={row.value}>{row.value}</span>,
      className: 'center-text',
    },
  ];

  if (props.ownRole !== 'patient') {
    columns = [
      {
        Header: '',
        id: 'edit',
        maxWidth: 70,
        className: 'center-text',
        sortable: false,
        accessor: (v) => null,
        Cell: (row) => (
          <>
            <Icon src="pencil" alt="Edit" width="15px" onClick={() => props.onEdit(row.original)} />
            <Icon
              src="trash"
              alt="Remove"
              width="15px"
              className="ml-3"
              onClick={() => props.onClickedDelete(row.original)}
            />
          </>
        ),
      },
      ...columns,
      {
        Header: 'Paid',
        id: 'paid',
        maxWidth: 60,
        accessor: ({ value }) => (value.paid ? 1 : 0),
        Cell: (row) => (row.value ? <Icon src="check" className="green" /> : null),
        className: 'center-text',
      },
    ];
  }
  return columns;
}

const DateCell = ({ immunization }) => {
  let {
    value: { given_date, scheduled_date },
  } = immunization;
  let icon = CPPUtils.getImmunizationIcon(immunization);
  let alt = CPPUtils.getImmunizationIconDescription(immunization);
  return (
    <span className="center-text">
      {icon.src ? <Icon {...icon} width="15px" alt={alt} /> : null}
      {given_date ? CalendarUtils.format(given_date) : CalendarUtils.format(scheduled_date)}
    </span>
  );
};

const AgeCell = ({ immunization, patient }) => {
  let {
    value: { given_date, scheduled_date },
  } = immunization;

  let date = given_date || scheduled_date;
  let age = 'N/A';
  let { date_of_birth } = patient || {};

  if (date_of_birth !== undefined && date_of_birth !== 0) {
    age = CalendarUtils.calculateAge(date_of_birth, date, true);
  }

  return <span>{age}</span>;
};

const VaccineNames = () => (
  <div className="vaccine-names">
    {DISEASES.map((description) => (
      <span key={description.name}>{description.name}</span>
    ))}
  </div>
);

const TopButtons = (props) => (
  <div className="row center-text button-wrapper">
    <ExportButton {...props} />
    <ViewAllButton {...props} />
    {props.ownRole !== 'patient' && <GenerateButton {...props} />}
  </div>
);

const ExportButton = ({ patient }) => {
  const linkRef = useRef();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);

  async function exportPdf() {
    setLoading(true);
    const result = await dispatch(
      api.HealthRecords.exportImmunizations({ patientId: patient.id }),
      { errorTitle: 'Immunizations Export' },
    );

    if (!result) {
      return;
    }

    const url = MiscUtils.getBlobUrl(result);

    const a = linkRef.current;
    a.download = 'immunizations.pdf';
    a.href = url;
    a.click();
    a.href = '';
    setLoading(false);
  }

  return (
    <div>
      <a href="careand.ca" style={{ display: 'none' }} ref={linkRef}>
        Download
      </a>
      <DefaultButton
        name="Export PDF"
        loading={loading}
        icon="file-pdf"
        onClick={exportPdf}
        style={{ width: '150px' }}
      />
    </div>
  );
};

const ViewAllButton = (props) => {
  let message = props.showAllImmunizations
    ? 'Hide Future Immunizations'
    : 'Show Future Immunizations';
  return (
    <div>
      <DefaultButton
        name={message}
        onClick={props.showAllButtonClicked}
        style={{ width: '220px' }}
      />
    </div>
  );
};

const GenerateButton = (props) => (
  <div>
    <DefaultButton
      name="Generate Scheduled Immunizations"
      onClick={props.onGenerate}
      loading={props.generating}
      className="blue"
      style={{ width: '260px' }}
    />
  </div>
);

function getVaccinesColumns() {
  return DISEASES.map((d) => {
    return {
      Header: '',
      maxWidth: 25,
      id: d.name,
      className: 'center-text',
      accessor: (i) => {
        let icon = isChecked(i, d);
        return icon ? (
          <Icon
            fontAwesomeStyle="fas"
            src={icon.src}
            className={icon.className}
            width="10px"
            style={{ left: 0, top: 0 }}
          />
        ) : null;
      },
    };
  });
}

function isChecked(immunization, disease) {
  let icon = undefined;

  if (CPPUtils.isOverdueImmunization(immunization)) icon = { src: 'circle', className: 'red' };
  if (CPPUtils.isGivenImmunization(immunization)) icon = { src: 'check', className: 'green' };

  if (icon) {
    let matchesDisease =
      _.indexOf(disease.immunizations, immunization.value.name.toLowerCase()) !== -1;
    if (matchesDisease) return icon;
  }
}

// Re-export so existing consumers importing { DISEASES } from this module keep working
// (e.g. AppointmentNotes/NewAppointmentNote/Flow/flows.jsx). The data itself now lives
// in ./diseases so it can be unit-tested without the React/Redux/Vite import graph.
export { DISEASES };

export default YellowCardView;
